import { Box, IconButton, Menu, MenuItem, Tooltip, Typography, useTheme } from "@mui/material";
import { RequestFormContext } from "../RequestFormContext";
import { getCenterStyles, SnackbarType } from "vig-ui-library";
import { RequestFileDto } from "../Schemas/RequestFileDto";
import { IoClose } from "react-icons/io5";
import { BiTransfer } from "react-icons/bi";
import { ImageContainer } from "./ImageContainer";
import { getRequestWithFiles } from "../Utilities/mappers";
import { useDeleteFile } from "../Hooks/useDeleteFile";
import { useChangeFileTaxonomy } from "../Hooks/useChangeFileTaxonomy";
import { extensionTester } from "../Utilities/file";
import { downloadStreamFile, openPdfInNewTab } from "../Utilities/streamFileClient";
import { handleDevelopmentError } from "../Utilities/error";
import { TaxonomyDto } from "../Schemas/TaxonomyDto";
import { getAttachmentFileTaxonomyId } from "../Utilities/fileTaxonomy";
import { useMemo, useState, type MouseEvent } from "react";

const THUMB_SIZE = 102;
const TILE_SHADOW = "0px 0px 10px 0px #00000040";

export type FileTileProps = Omit<RequestFormContext, "pathToProp"> & {
    file: RequestFileDto;
    primaryLabel: string; // taxonomy.name or sideLabel
    index?: number; // for index-based operations
    showDeleteButton?: boolean;
    /** When true, shows taxonomy menu if `changeTaxonomyTargets` has at least one other category. */
    showChangeTaxonomyButton?: boolean;
    /** Visible attachment categories the file may be moved to (current category is omitted in the menu). */
    changeTaxonomyTargets?: TaxonomyDto[];
    deleteButtonCondition?: (file: RequestFileDto, context: Omit<RequestFormContext, "pathToProp">) => boolean;
    clickedStateStrategy?: "index" | "id"; // how to manage clicked state
};

export const FileTile = (props: FileTileProps) => {
    const theme = useTheme();
    const {
        file,
        primaryLabel,
        index,
        showDeleteButton = true,
        showChangeTaxonomyButton = false,
        changeTaxonomyTargets,
        deleteButtonCondition,
        clickedStateStrategy = "id",
        ...context
    } = props;

    const [taxonomyMenuAnchor, setTaxonomyMenuAnchor] = useState<null | HTMLElement>(null);

    const taxonomyMenuOptions = useMemo(
        () =>
            (changeTaxonomyTargets ?? []).filter((t) => t.id !== getAttachmentFileTaxonomyId(file)),
        [changeTaxonomyTargets, file.taxonomy?.id, file.taxonomyId],
    );

    const { changeToTaxonomy, isChangingTaxonomy } = useChangeFileTaxonomy({
        file,
        request: props.request,
        requestApi: props.requestApi,
        openSnackbar: props.openSnackbar,
        rerenderForm: props.rerenderForm,
        onlySaveRequest: props.onlySaveRequest,
    });

    const { handleDelete } = useDeleteFile({
        file,
        request: props.request,
        requestApi: props.requestApi,
        openConfirmModal: props.openConfirmModal,
        openSnackbar: props.openSnackbar,
        rerenderForm: props.rerenderForm,
        removeFileStrategy: index !== undefined ? "index" : "id",
        fileIndex: index,
    });

    const onClickFile = () => {
        if (file.uploadCompleted) {
            file.clicked = !file.clicked;

            const requestWithFiles = getRequestWithFiles(props.request);

            if (clickedStateStrategy === "index" && index !== undefined) {
                let i = 0;
                requestWithFiles.current.files.forEach((f) => {
                    if (f.id !== file.id && i > index) f.clicked = false;
                    i++;
                });
            } else {
                requestWithFiles.current.files.forEach((f) => {
                    if (f.id !== file.id) f.clicked = false;
                });
            }
        }
        props.rerenderForm();

        if (!file.uploadCompleted || !file.id) {
            return;
        }

        void (async () => {
            try {
                if (extensionTester.fileIsPdf(file.extension || "")) {
                    await openPdfInNewTab(props.axiosInstance, props.requestModuleUrl, file, props.apiRouteConfig);
                } else {
                    await downloadStreamFile(props.axiosInstance, props.requestModuleUrl, file, props.apiRouteConfig);
                }
            } catch (err) {
                handleDevelopmentError(err);
                props.openSnackbar({
                    text: "Nepodarilo sa otvoriť alebo stiahnuť súbor.",
                    type: SnackbarType.ERROR,
                });
            }
        })();
    };

    const shouldShowDeleteButton = deleteButtonCondition
        ? deleteButtonCondition(file, context)
        : showDeleteButton && file.uploadCompleted;

    const showTaxonomyMenu =
        showChangeTaxonomyButton &&
        !!file.uploadCompleted &&
        !!file.id &&
        taxonomyMenuOptions.length > 0;

    const openTaxonomyMenu = (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        setTaxonomyMenuAnchor(e.currentTarget);
    };

    const closeTaxonomyMenu = () => {
        setTaxonomyMenuAnchor(null);
    };

    const ACTION_BTN_SIZE = 24;
    const ACTION_BTN_GAP = 4;

    const removeButtonSx = {
        position: "absolute" as const,
        top: 8,
        right: 8,
        width: ACTION_BTN_SIZE,
        height: ACTION_BTN_SIZE,
        padding: 0,
        backgroundColor: theme.palette.common.white,
        color: theme.palette.common.black,
        boxShadow: TILE_SHADOW,
        "&:hover": {
            backgroundColor: theme.palette.common.white,
            color: theme.palette.common.black,
            boxShadow: TILE_SHADOW,
        },
    };

    /** Stacked under the delete control when both are visible; same column as delete (top-right). */
    const taxonomyButtonTop = shouldShowDeleteButton ? 8 + ACTION_BTN_SIZE + ACTION_BTN_GAP : 8;

    return (
        <Box
            sx={{
                position: "relative",
                width: THUMB_SIZE,
                height: THUMB_SIZE,
                flexShrink: 0,
            }}
        >
            <Box
                onClick={onClickFile}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onClickFile();
                    }
                }}
                aria-label={`Otvoriť alebo stiahnuť súbor — ${primaryLabel}`}
                sx={{
                    width: THUMB_SIZE,
                    height: THUMB_SIZE,
                    borderRadius: "7px",
                    overflow: "hidden",
                    boxShadow: TILE_SHADOW,
                    cursor: "pointer",
                    ...getCenterStyles(),
                }}
            >
                <ImageContainer
                    file={file}
                    requestModuleUrl={props.requestModuleUrl}
                    axiosInstance={props.axiosInstance}
                    apiRouteConfig={props.apiRouteConfig}
                    isPreview={true}
                />
            </Box>

            {shouldShowDeleteButton && (
                <Tooltip title={<Typography variant="body2" color={theme.palette.common.white}>Vymazať súbor</Typography>}>
                    <IconButton
                        size="small"
                        aria-label="Vymazať súbor"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDelete();
                        }}
                        sx={{
                            ...removeButtonSx,
                            borderRadius: "50%",
                        }}
                    >
                        <IoClose size={16} color="currentColor" aria-hidden />
                    </IconButton>
                </Tooltip>
            )}

            {showTaxonomyMenu && (
                <>
                    <Tooltip title={<Typography variant="body2" color={theme.palette.common.white}>
                        Zmeniť kategóriu prílohy
                    </Typography>}>
                        <IconButton
                            size="small"
                            aria-label="Zmeniť kategóriu prílohy"
                            onClick={openTaxonomyMenu}
                            aria-haspopup="true"
                            aria-expanded={taxonomyMenuAnchor ? "true" : "false"}
                            sx={{
                                ...removeButtonSx,
                                top: taxonomyButtonTop,
                            }}
                        >
                            <BiTransfer size={16} color="currentColor" aria-hidden />
                        </IconButton>
                    </Tooltip>
                    <Menu
                        anchorEl={taxonomyMenuAnchor}
                        open={Boolean(taxonomyMenuAnchor)}
                        onClose={closeTaxonomyMenu}
                        onClick={(e) => e.stopPropagation()}
                        PaperProps={{
                            sx: { maxHeight: 320, maxWidth: 400 },
                        }}
                        MenuListProps={{ dense: true }}
                    >
                        {taxonomyMenuOptions.map((taxonomy) => (
                            <MenuItem
                                key={taxonomy.id}
                                disabled={isChangingTaxonomy}
                                onClick={() => {
                                    closeTaxonomyMenu();
                                    void changeToTaxonomy(taxonomy.id);
                                }}
                            >
                                <Typography variant="body2" noWrap sx={{ maxWidth: 360 }}>
                                    {`Zmeniť na „${taxonomy.name ?? ""}"`}
                                </Typography>
                            </MenuItem>
                        ))}
                    </Menu>
                </>
            )}
        </Box>
    );
};
